import XLSX, { ColInfo, Comments, Sheet, WorkBook } from '@sheet/edit';
import isMobilePhone from 'validator/lib/isMobilePhone';

import {
  DATA_START_ROW,
  ERROR_TOO_MUCH_ROWS,
  HEADER_ROW_COUNT,
} from '@app/features/bulkRequest/constant';
import { findLatestExcelDataRowNumber, numberToColumnName } from '@app/libs/excel';
import getFormattedLocale from '@app/libs/excel/bulkRequest/getFormattedLocale';
import { removeZeroWidthWhiteSpace } from '@app/libs/inputFormat';
import Column from '@app/store/bulkRequest/form/Column';
import ColumnType from '@app/store/bulkRequest/form/ColumnType';
import FormValue from '@app/store/transport-layer/bulk-request/bulk-requests/request-tasks/FormValue';

import getExcelColumnTitleText from './getExcelColumnTitleText';
import getPhoneNumberFormattedCarbonCopyContacts from './getPhoneNumberFormattedCarbonCopyContacts';
import getRemoveDuplicatedArray from './getRemoveDuplicated';

type BulkRequestDataCategoryType =
  | '서명자 설정'
  | '추가 인증 수단 및 기타 설정'
  | '참조자 설정'
  | '추가 내용';

const categoryMapper: {
  columnType: ColumnType;
  category: BulkRequestDataCategoryType;
  comment?: string;
}[] = [
  {
    columnType: 'DOCUMENT_TITLE',
    category: '서명자 설정',
    comment:
      '※ 문서명을 모두 동일하게 설정할 경우, 체결 이후 검색과 관리가 어려워질 수 있습니다. \n' +
      '※ 문서명 뒤에 서명자 이름 등으로 설정하면 체결 이후 검색과 관리가 쉬워집니다. \n' +
      '\n' +
      '예시) \n' +
      '2023 대량전송 문서 - 김모두',
  },
  {
    columnType: 'PARTICIPANT_NAME',
    category: '서명자 설정',
  },
  {
    columnType: 'PARTICIPANT_CONTACT',
    category: '서명자 설정',
  },
  {
    columnType: 'PARTICIPANT_LOCALE',
    category: '서명자 설정',
    comment:
      '※ 설정한 언어로 안내 메일 및 감사추적인증서가 발송됩니다. 미설정 시 기본 한국어로 안내됩니다.\n' +
      '지원언어 : 영어, 한국어',
  },
  {
    columnType: 'MESSAGE_FROM_REQUESTER',
    category: '서명자 설정',
  },
  {
    columnType: 'VERIFICATION_PASSWORD',
    category: '추가 인증 수단 및 기타 설정',
    comment:
      '※ 접근암호를 서명자에게 미리 알려주세요.\n' +
      '설정된 암호는 이후 변경, 확인이 어려우니 신중하게 설정해주세요.',
  },
  {
    columnType: 'VERIFICATION_PASSWORD_HINT',
    category: '추가 인증 수단 및 기타 설정',
    comment: "※ 비밀번호 힌트는 30자 이내로 입력해주세요.\n 예시) '휴대전화번호 뒤 4자리'",
  },
  {
    columnType: 'VERIFICATION_MOBILE_IDENTIFICATION_NAME',
    category: '추가 인증 수단 및 기타 설정',
    comment:
      '※ 휴대폰 명의의 서명자 실명을 입력해 주세요.\n' +
      '※ 휴대폰본인인증으로 인증하는 경우, 법인공동인증서 인증은 중복 사용이 불가합니다.',
  },
  {
    columnType: 'VERIFICATION_MOBILE_IDENTIFICATION_PHONE',
    category: '추가 인증 수단 및 기타 설정',
  },
  {
    columnType: 'PDF_PASSWORD',
    category: '추가 인증 수단 및 기타 설정',
    comment:
      '※ 서명이 모두 완료된 문서 파일을 열람할 때 사용할 비밀번호를 6자 ~ 32자 이내 영문, 숫자, 특수문자로 설정해 주세요. \n' +
      '※ 서명이 모두 완료된 PDF 문서에 비밀번호가 설정됩니다. \n' +
      '※ 서명이 모두 완료된 문서 파일을 열람할 때 해당 비밀번호를 입력해야 합니다. \n' +
      '※ 서명 이후 서명자, 참조자, 진행자에게 설정한 비밀번호를 꼭 전달해 주세요 \n' +
      '※ 비밀번호는 요청자 본인만 알 수 있으니, 별도로 기록해 두세요. \n' +
      '※ 잠금 설정된 문서는 추후 입력값 추출이 불가능합니다. \n',
  },
  {
    columnType: 'VERIFICATION_D_CERT_NAME',
    category: '추가 인증 수단 및 기타 설정',
    comment:
      '서명자가 법인인 경우 공동인증서로 인증 후, 서명할 수 있습니다.\n' +
      '※ 정확한 법인명과 사업자등록번호를 입력해 주세요.\n' +
      '※ 법인공동인증서로 인증하는 경우, 휴대폰본인인증은 중복 사용이 불가합니다.\n' +
      '※ 카카오톡 서명자에게는 법인 공동인증서 인증을 설정할 수 없습니다.',
  },
  {
    columnType: 'VERIFICATION_D_CERT_BIZ_NUMBER',
    category: '추가 인증 수단 및 기타 설정',
  },
  {
    columnType: 'CARBON_COPY_CONTACT',
    category: '참조자 설정',
    comment:
      "※ 참조자가 여러명일 경우 ','로 구분해 주세요.\n" +
      '※ 참조자는 최대 10명까지 설정 가능합니다.',
  },
  {
    columnType: 'CARBON_COPY_LOCALE',
    category: '참조자 설정',
    comment:
      '※ 설정한 언어로 참조자에게 안내 메일 및 감사추적인증서가 발송됩니다. \n' +
      "※ 참조자 순서에 맞게 ','로 구분 해주세요. \n" +
      '미설정 시 기본 한국어로 안내됩니다.\n' +
      '지원언어 : 영어, 한국어',
  },
  {
    columnType: 'FIELD_TEXT',
    category: '추가 내용',
  },
  { columnType: 'FIELD_CHECKBOX', category: '추가 내용' },
];
const colorMapper: Record<
  BulkRequestDataCategoryType | 'default',
  { categoryColor: string; headerColor: string }
> = {
  '서명자 설정': {
    categoryColor: 'c5deb5',
    headerColor: 'e3eeda',
  },
  '추가 인증 수단 및 기타 설정': {
    categoryColor: 'b5c7e7',
    headerColor: 'dfebf6',
  },
  '참조자 설정': {
    categoryColor: 'ffe699',
    headerColor: 'fff3cc',
  },
  '추가 내용': {
    categoryColor: 'f9c6a6',
    headerColor: 'fbe2be',
  },
  default: {
    categoryColor: 'ffffff',
    headerColor: 'ffffff',
  },
};

/**
 * 조회 된 대량 전송 양식을 엑셀 파일로 저장 합니다.
 */
export function saveBulkRequestFormExcelFile({
  columns,
  fileName,
  samples,
}: {
  columns: Column[];
  fileName: string;
  samples: string[][];
}) {
  const isContainRequesterInput = columns.some(
    (column) => column.type === 'FIELD_TEXT' || column.type === 'FIELD_CHECKBOX'
  );

  const filteredCategoryMapper = categoryMapper.filter((mapper) =>
    columns.some((column) => column.type === mapper.columnType)
  );

  // 카테고리가 표시되는 행의 인덱스를 저장합니다.
  const categoryStartColumnNumbers = filteredCategoryMapper.reduce<number[]>(
    (startingNumbers, currentColumn, currentIndex) => {
      // 요청자 필드가 없을 경우 추가 내용 카테고리 헤더를 표시하지 않습니다.
      if (!isContainRequesterInput && currentColumn.category === '추가 내용') {
        return startingNumbers;
      }
      return currentColumn.category !==
        filteredCategoryMapper[startingNumbers[startingNumbers.length - 1]].category
        ? [...startingNumbers, currentIndex]
        : startingNumbers;
    },
    [0]
  );

  // 워크북 만들기
  const workBook = XLSX.utils.book_new();

  // 엑셀 헤더 Row 만들기
  const headerDataRow = columns.reduce<Sheet>((headerRow, currentColumn, idx) => {
    const currentHeader = filteredCategoryMapper.find(
      (column) => column.columnType === currentColumn.type
    );

    const comment: Comments = [
      {
        a: '모두싸인',
        R: [
          {
            t: 's',
            v: currentHeader?.comment || '',
            s: { bold: true, sz: 10 },
          },
        ],
      },
    ];
    comment.hidden = true;
    comment.s = { fgColor: { rgb: 'FFFCCC' } };
    const lineCount = currentHeader?.comment?.match(/[\n]/g)?.length || 0;
    comment['!pos'] = {
      x: 200 * (idx + 1),
      y: 40,
      w: 340,
      h: (lineCount + 2) * 30,
    };

    headerRow[numberToColumnName(idx)] = {
      t: 's',
      v: getExcelColumnTitleText(currentColumn),
      s: {
        bottom: { style: 'medium' },
        left: { style: 'thin' },
        right: { style: 'thin' },
        top: { style: 'thin' },
        alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
        fgColor: {
          rgb: colorMapper[currentHeader?.category ?? 'default'].headerColor,
        },
      },
      ...(currentHeader?.comment && {
        c: comment,
      }),
    };

    return headerRow;
  }, {});

  const columnInfos: ColInfo[] = columns.map(() => ({ wpx: 200 }));

  // 샘플 데이터 추가
  const sampleDataRowList = samples.map((sample) =>
    Object.fromEntries(
      sample.map((columnValue, index) => [
        numberToColumnName(index),
        {
          v: columnValue,
          t: 's',
        },
      ])
    )
  );

  const headerColumns = columns.map((_id, index) => numberToColumnName(index));

  // 첫번째 행에서 카테고리 표시할 셀 지정
  const categoryHeaderRow = categoryStartColumnNumbers.reduce<Sheet>(
    (categoryRow, currentIndex, idx) => {
      const currentCategory = filteredCategoryMapper[currentIndex].category;
      categoryRow[numberToColumnName(categoryStartColumnNumbers[idx])] = {
        t: 's',
        v: currentCategory,
        s: {
          right: { style: 'thin' },
          left: { style: 'thin' },
          alignment: { horizontal: 'center' },
          bold: true,
          fgColor: { rgb: colorMapper[currentCategory].categoryColor },
        },
      };
      return categoryRow;
    },
    {}
  );

  // 워크시트 만들기
  const workSheet = XLSX.utils.json_to_sheet(
    [categoryHeaderRow, headerDataRow, ...sampleDataRowList],
    {
      header: headerColumns,
      skipHeader: true,
    }
  );
  workSheet['!cols'] = columnInfos;
  workSheet['!merges'] = categoryStartColumnNumbers.map((_, index) => {
    if (index === categoryStartColumnNumbers.length - 1) {
      return {
        s: { r: 0, c: categoryStartColumnNumbers[index] },
        e: { r: 0, c: columns.length - 1 },
      };
    }
    return {
      s: { r: 0, c: categoryStartColumnNumbers[index] },
      e: { r: 0, c: categoryStartColumnNumbers[index + 1] - 1 },
    };
  });

  // 워크시트를 워크북에 추가하기
  XLSX.utils.book_append_sheet(workBook, workSheet);

  // 워크북을 파일로 저장하기
  XLSX.writeFile(workBook, fileName, { cellStyles: true });
}

const getWorkBookInfo = (workBook: XLSX.WorkBook) => {
  const { SheetNames, Sheets } = workBook;
  const sheet = Sheets[SheetNames[0]];

  return {
    sheetNames: SheetNames,
    sheet,
    isOutdatedExcelForm: sheet?.A1?.v?.trim() !== '서명자 설정',
  };
};

/**
 * 엑셀데이터를 읽어서 서버로 전송하기 위한 데이터를 만듭니다.
 */
export function createFormValues({
  form,
  workBook,
}: {
  form: Column[];
  workBook: WorkBook;
}): FormValue[] {
  const { sheetNames, sheet, isOutdatedExcelForm } = getWorkBookInfo(workBook);

  if (sheetNames.length < 1) {
    throw new Error('엑셀 파일에서 시트를 확인할 수 없습니다.');
  }

  if (isOutdatedExcelForm) {
    throw new Error(
      `엑셀 파일 형식이 다르거나, A1 칼럼명이 올바른지 확인해 주세요. A1 칼럼명이 변경되었거나, 칼럼 내 공백이 있는 경우 업로드가 불가합니다. 설정용 엑셀 파일을 다시 다운로드해 주세요.`
    );
  }

  const ref = sheet['!ref'];

  if (!ref) {
    throw new Error('excel ref is not exist!');
  }

  const latestExcelDataRowNumber = findLatestExcelDataRowNumber({
    dataStartRow: DATA_START_ROW,
    formColumnCount: form.length,
    sheet,
  });

  if (latestExcelDataRowNumber < DATA_START_ROW) {
    throw new Error('엑셀 파일에서 데이터를 확인할 수 없습니다.');
  }

  if (latestExcelDataRowNumber - HEADER_ROW_COUNT > 5000) {
    throw new Error(ERROR_TOO_MUCH_ROWS);
  }

  // 엑셀 시트를 탐색하기 위해 excelSheetRowNumberList 배열을 만듭니다.
  const excelSheetRowNumberList = Array.from(
    { length: latestExcelDataRowNumber },
    (_v, index) => index + 1
  ).filter((rowNumber) => rowNumber >= DATA_START_ROW);

  return excelSheetRowNumberList.map(
    (rowNumber): FormValue =>
      form.map((column, columnIndex): string => {
        const excelSheetCell = sheet[`${numberToColumnName(columnIndex)}${rowNumber}`];
        let excelSheetCellValue = excelSheetCell ? String(excelSheetCell.w).trim() : '';

        /**
         * 요청자 필드 체크박스의 데이터를 변경합니다.
         * 대소문자 구분없이 O, X, true, false를 입력하면
         * 대문자 TRUE, FALSE로 변경해서 저장합니다.
         * 여기서는 데이터를 생성하고, 유효성 검사(validateFormValues) 단계에서
         * 올바른 값이 아닌 경우 알맞은 값이 아니라고 이용자에게 경고합니다.
         */
        if (column.type === 'FIELD_CHECKBOX') {
          excelSheetCellValue = excelSheetCellValue.toUpperCase();
          if (excelSheetCellValue === 'O') {
            excelSheetCellValue = 'TRUE';
          }
          if (excelSheetCellValue === 'X') {
            excelSheetCellValue = 'FALSE';
          }
        }

        /**
         * 엑셀 파일에서 ALT + ENTER 키로 줄바꿈하면 \r\n 으로 줄바꿈이 진행됩니다.
         * 이 문제로 대량전송 미리보기와 PDF 생성 결과가 달라지는 문제가 있습니다.
         * 여기서 일반적인 줄바꿈인 \n만 남기고 \r 문자는 제거합니다.
         */
        if (column.type === 'FIELD_TEXT') {
          excelSheetCellValue = excelSheetCellValue.replace(/\r/g, '');
        }

        if (
          column.type === 'PARTICIPANT_CONTACT' ||
          column.type === 'VERIFICATION_MOBILE_IDENTIFICATION_PHONE'
        ) {
          /**
           * ZeroWidthSpace를 제거하지 않으면 isMobilePhone에서 휴대폰번호가 아니라고
           * 판단하여 제대로 처리가 안되는 케이스를 발견했습니다.
           * 먼저 특수문자 공백을 제거 후 휴대폰 번호는 숫자만 입력되게 수정합니다.
           */
          excelSheetCellValue = removeZeroWidthWhiteSpace(excelSheetCellValue);
          const isMobile = isMobilePhone(excelSheetCellValue, 'ko-KR');
          if (isMobile) {
            excelSheetCellValue = excelSheetCellValue.replace(/[^0-9]/g, '');
          }
          // 전화번호가 셀서식 문제로 1012341234 형식으로 입력되면 제일 앞에 '0' 문자를 추가합니다.
          if (isMobilePhone(excelSheetCellValue, 'ko-KR') && excelSheetCellValue.startsWith('1')) {
            excelSheetCellValue = `0${excelSheetCellValue}`;
          }
        }

        if (column.type === 'VERIFICATION_D_CERT_BIZ_NUMBER') {
          excelSheetCellValue = excelSheetCellValue.replace(/[-\s]/g, '');
        }

        /**
         * 공백제거가 필요한 항목들 공백을 제거합니다.
         */
        if (
          column.type === 'DOCUMENT_TITLE' ||
          column.type === 'PARTICIPANT_CONTACT' ||
          column.type === 'VERIFICATION_MOBILE_IDENTIFICATION_NAME' ||
          column.type === 'VERIFICATION_MOBILE_IDENTIFICATION_PHONE' ||
          column.type === 'PARTICIPANT_LOCALE' ||
          column.type === 'VERIFICATION_D_CERT_NAME' ||
          column.type === 'VERIFICATION_D_CERT_BIZ_NUMBER'
        ) {
          excelSheetCellValue = removeZeroWidthWhiteSpace(excelSheetCellValue).trim();
        }

        /**
         * 이메일 주소에서 시각적으로 식별이 불가능한 특수문자 '＠'를 키보드로 입력하는 '@'로 변경합니다.
         */
        if (column.type === 'PARTICIPANT_CONTACT') {
          excelSheetCellValue = excelSheetCellValue.replace(/＠/g, '@');
        }

        if (column.type === 'PARTICIPANT_LOCALE') {
          excelSheetCellValue = getFormattedLocale(excelSheetCellValue);
        }

        /**
         * 참조자 항목은 콤마(,)로 구분하고 있어서 파싱 후 공백 및 중복 연락처를 제거합니다.
         */
        if (column.type === 'CARBON_COPY_CONTACT') {
          excelSheetCellValue = getRemoveDuplicatedArray(
            getPhoneNumberFormattedCarbonCopyContacts(
              excelSheetCellValue
                .split(',')
                .map((contact) => removeZeroWidthWhiteSpace(contact).trim())
                // 제일 마지막에 콤마(,)가 있거나 중복입력된 경우 제거합니다.
                .filter((contact) => !!contact)
            )
          ).join(',');
        }
        if (column.type === 'CARBON_COPY_LOCALE') {
          const carbonCopyLocaleList = excelSheetCellValue
            .split(',')
            .map((locale) => removeZeroWidthWhiteSpace(locale).trim())
            .filter((locale) => !!locale);

          excelSheetCellValue = carbonCopyLocaleList
            .map((locale) => {
              return getFormattedLocale(locale);
            })
            .join();
        }

        return excelSheetCellValue;
      })
  );
}
